import React, { useMemo } from "react";
import { useSeo } from "@/hooks/useSeo";
import { Link } from "react-router-dom";
import useApiList from "@/hooks/useApiList";
import { ArrowRight, Check, Mail } from "lucide-react";
import { creditLabel, GCSB_CREDITS } from "@/data/credits";

const PATHWAY = [
  { n: 1, level: `Foundation · ${creditLabel("CSCE")} · 150 h`, code: "CSCE", name: "Converged Security Cyber Expert", blurb: "Foundational knowledge in converged security, governance, risk, incident response, regulatory compliance, resilience and security leadership fundamentals." },
  { n: 2, level: `Professional · ${creditLabel("CSP")} · 200 h`, code: "CSP", name: "Converged Security Professional", blurb: "Operational implementation across technology integration, SIEM/PSIM, IAM, OT security, security operations, data protection, threat detection and organisational readiness." },
  { n: 3, level: `Leadership · ${creditLabel("CSL")} · 200 h`, code: "CSL", name: "Chartered Converged Security Leader", blurb: "Leadership and governance implementation across cross-domain management, resilience, regulatory strategy, crisis leadership, security culture and transformation." },
  { n: 4, level: `Executive · ${creditLabel("CCSO")} · 200 h`, code: "CCSO", name: "Chief Converged Security Officer", blurb: "Executive security governance, board oversight, enterprise risk, strategic resilience leadership, security transformation and assurance." },
  { n: 5, level: `Capstone · ${creditLabel("EIP")} · 150 h`, code: "EIP", link: "PGDCSL", name: "Executive Integration Portfolio", blurb: `Executive Integration Portfolio - ${creditLabel("EIP")}. Capstone assessment integrating governance, leadership, risk, resilience, security transformation and executive decision-making, completing the PGDCSL (${creditLabel("PGDCSL")} in total, 900 hours).` },
];

const COMPONENTS = [
  { badge: `Foundation · ${creditLabel("CSCE")}`, code: "CSCE", name: "CSCE - Converged Security Cyber Expert", desc: "Foundational knowledge in converged security fundamentals, governance principles, risk management, incident response, regulatory compliance, organisational resilience and security leadership fundamentals.", meta: `${creditLabel("CSCE")} · 150 Indicative Learning Hours · MCSI · €250 · Prof. Dr. Vladimir Bunic` },
  { badge: `Professional · ${creditLabel("CSP")}`, code: "CSP", name: "CSP - Converged Security Professional", desc: "Operational and implementation capability across technology integration, SIEM/PSIM, identity and access management, OT security, security operations, data protection, threat detection and organisational readiness.", meta: `${creditLabel("CSP")} · 200 Indicative Learning Hours · MCSI · €750 · Prof. Dr. Vladimir Bunic` },
  { badge: `Leadership · ${creditLabel("CSL")}`, code: "CSL", name: "CSL - Chartered Converged Security Leader", desc: "Leadership and governance implementation across security leadership, cross-domain management, resilience, regulatory strategy, crisis leadership, security culture and transformation management.", meta: `${creditLabel("CSL")} · 200 Indicative Learning Hours · MCSL (Chartered) · Direct Assessment €200` },
  { badge: `Executive · ${creditLabel("CCSO")}`, code: "CCSO", name: "CCSO - Chief Converged Security Officer", desc: "Executive-level capability across security governance, board-level oversight, enterprise risk, strategic resilience leadership, security transformation, executive communications, compliance governance and performance assurance.", meta: `${creditLabel("CCSO")} · 200 Indicative Learning Hours · FCSI (Fellow) · Direct Chartered Executive Assessment €500 · Prof. Dr. Vladimir Bunic` },
  { badge: `Postgraduate · ${creditLabel("PGDCSL")}`, code: "PGDCSL", name: "PGDCSL - Postgraduate Executive Diploma in Converged Security Leadership", desc: `CSI's highest integrated qualification, awarded after CSCE, CSP, CSL, CCSO and the assessed Executive Integration Portfolio (EIP, ${creditLabel("EIP")} / 150 hours capstone).`, meta: `${creditLabel("PGDCSL")} · 900 Indicative Learning Hours · 24–36 months · From €995 (Founding Cohort) up to €2,995` },
];

const POLICY_SECTIONS = [
  {
    id: "eligibility",
    title: "§ 1 - Eligibility",
    items: [
      { k: "CSCE", v: "Open to all security practitioners. No formal prerequisites - designed as the entry point to the CSI certification pathway." },
      { k: "CSP", v: "Minimum 1 year of relevant professional experience in security or a related discipline required. CSCE pathway preferred." },
      { k: "CSL", v: "CSCE and CSP are the standard preceding components. Direct Chartered Assessment available for leaders with 15+ years' experience, including 5+ years in leadership responsibility." },
      { k: "CCSO", v: "Direct Chartered Executive Assessment for serving C-suite leaders with 15+ years' experience; CISSP, CPP, or equivalent recognised qualification preferred; all candidates subject to CSI Certification & Standards Committee review." },
    ],
  },
  {
    id: "assessment",
    title: "§ 2 - Assessment & Award",
    items: [
      { k: "CSCE", v: "Group scenario-based assessment across 7 modules. No written examination. Award: CSI Digital Certificate and verifiable badge (3-year validity). Exam-only route available via partner portal." },
      { k: "CSP", v: "Written and practical assessment administered by CSI. Passing standard confirmed per programme framework. Exam-only route available via partner portal." },
      { k: "CSL", v: "Direct Chartered Assessment: portfolio of evidence, executive assessment interview and panel review via CSI's partner portal. Governed as the leadership component of the PGDCSL framework." },
      { k: "CCSO", v: "Direct Chartered Executive Assessment: panel review and executive integration case via partner portal. Covers all 22 capability domains. Certificates issued within 14 working days of confirmed completion." },
      { k: "PGDCSL", v: "Completion requires CSCE, CSP, CSL, CCSO and the assessed Executive Integration Portfolio (EIP). Weighted scoring: CSCE 15% · CSP 20% · CSL 20% · CCSO 25% · EIP 20%. Classification: Distinction 90–100 · Merit 80–89 · Pass 70–79." },
    ],
  },
  {
    id: "validity",
    title: "§ 3 - Validity & Renewal",
    items: [
      { v: "CSI designations remain subject to good standing, professional conduct and applicable maintenance requirements." },
      { v: "Annual CPD requirements are designation-specific: CSCE 10h, CSP 15h, CSL 20h, CCSO 25h, PGDCSL 25h." },
      { v: "The PGDCSL award itself remains permanently recorded, while active certification, fellowship and professional standing may be suspended or revoked." },
      { v: "CSI may require refresher assessment, updated certification or CPD evidence where standards materially change." },
    ],
  },
  {
    id: "conduct",
    title: "§ 4 - Code of Conduct",
    items: [
      { v: "All certified professionals must uphold the CSI Code of Conduct and act with integrity in their professional practice at all times." },
      { v: "Misrepresentation of certification status, level, or credentials is grounds for immediate revocation." },
      { v: "CSI reserves the right to revoke any certification where conduct is found to be in breach of professional ethics, CSI standards, or applicable law." },
    ],
  },
  {
    id: "use",
    title: "§ 5 - Recognition & Use",
    items: [
      { v: "Official designations include CSCE, CSP, CSL, CCSO and PGDCSL. The PGDCSL post-nominal may be used after all valid CSI designations while the graduate remains in good standing." },
      { v: "CSI may maintain a verifiable register of PGDCSL graduates and certified professionals in accordance with its records and privacy framework." },
      { v: "Use of CSI certification marks, logos, or designations in commercial marketing or publications requires prior written consent from CSI." },
    ],
  },
  {
    id: "pricing",
    title: "§ 6 - Pricing & Enrolment",
    items: [
      { k: "CSCE", v: "€250 per participant (training + exam). Exam-only route via partner portal: €100." },
      { k: "CSP", v: "€750 per participant (training + exam). Exam-only route via partner portal: €250." },
      { k: "CSL", v: "Direct Chartered Assessment: €200 (via CSI's partner portal). Governed as the leadership component of the PGDCSL framework." },
      { k: "CCSO", v: "Direct Chartered Executive Assessment: €500 (via CSI's partner portal). 15+ years' senior security leadership experience required; subject to CSI Certification & Standards Committee review." },
      { k: "PGDCSL", v: "From €995 (Founding Cohort) to €2,995 (Government & Enterprise). Six pricing tiers - see tiered pricing on the programme page." },
      { k: "Bundle · CSCE + CSP", v: "€800 (20% saving)." },
      { v: "CSP live cohorts: scheduled quarterly · typical cohort size 5–10 participants. Direct Chartered Assessment (CSL / CCSO) and PGDCSL routes are enrolled on application." },
    ],
  },
  {
    id: "appeals",
    title: "§ 7 - Appeals & Complaints",
    items: [
      { v: "Candidates may appeal an assessment outcome in writing to info@convergedsecurity.es within 30 days of receiving their result." },
      { v: "Appeals are reviewed by an independent panel with no involvement in the original assessment. Decisions are final and binding." },
      { v: "Complaints regarding programme delivery must be submitted in writing to info@convergedsecurity.es within 14 days of the programme end date." },
    ],
  },
];

export default function EducationPage() {
  useSeo({ title: "Education & Qualification Pathway", description: "The CSI qualification pathway - CSCE, CSP, CSL, CCSO and the PGDCSL Postgraduate Executive Diploma in Converged Security Leadership.", path: "/education" });
  const { data: courses } = useApiList("/courses");
  const courseMap = useMemo(() => {
    const m = {};
    for (const c of courses) if (c.code) m[c.code] = c;
    return m;
  }, [courses]);

  const linkFor = (code) => courseMap[code] ? `/courses/${courseMap[code].slug || courseMap[code].id}` : "/courses";

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 lg:pt-20 lg:pb-14 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-4">Professional Development</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]" data-testid="education-h1">
              Converged Security <span className="italic text-brand-gold">Training Programmes</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-800 max-w-3xl">
              A structured qualification pathway from converged security foundations to postgraduate executive leadership.
            </p>
            <p className="mt-4 text-sm sm:text-base text-zinc-700 max-w-3xl leading-relaxed">
              CSI offers a governed professional qualification pathway designed for security practitioners, IT professionals,
              managers and organisational leaders. The pathway combines certification, applied assessment and executive
              integration so candidates can lead cyber, physical and operational technology security as one organisational function.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/courses" className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="education-view-programmes">
                View all programmes <ArrowRight size={16}/>
              </Link>
              <a href="#cert-policy" className="chip px-5 py-3" data-testid="education-view-policy">Certification Policy</a>
            </div>
          </div>
          <aside className="lg:col-span-4">
            <div className="card-warm p-6">
              <div className="eyebrow mb-2">At a glance</div>
              <ul className="space-y-2 text-sm">
                <li className="flex gap-2"><Check size={16} className="text-brand-gold shrink-0 mt-0.5"/> 5 integrated qualification components</li>
                <li className="flex gap-2"><Check size={16} className="text-brand-gold shrink-0 mt-0.5"/> {creditLabel("PGDCSL")} (complete PGDCSL)</li>
                <li className="flex gap-2"><Check size={16} className="text-brand-gold shrink-0 mt-0.5"/> 22 converged security capability domains</li>
                <li className="flex gap-2"><Check size={16} className="text-brand-gold shrink-0 mt-0.5"/> ISO 31000, 27001, 22301 & regulatory alignment</li>
                <li className="flex gap-2"><Check size={16} className="text-brand-gold shrink-0 mt-0.5"/> Lead faculty: Prof. Dr. Vladimir Bunic</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Pathway */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="eyebrow mb-1">Your Journey</div>
        <h2 className="font-display text-3xl sm:text-4xl">CSI Qualification Pathway</h2>
        <p className="text-zinc-700 max-w-3xl mt-2 text-sm sm:text-base">
          A cumulative progression from foundational expertise to postgraduate executive leadership.
          The complete PGDCSL comprises {creditLabel("PGDCSL")} across five mandatory components: CSCE {GCSB_CREDITS.CSCE}, CSP {GCSB_CREDITS.CSP}, CSL {GCSB_CREDITS.CSL}, CCSO {GCSB_CREDITS.CCSO} and the Executive Integration Portfolio {GCSB_CREDITS.EIP}.
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {PATHWAY.map((p) => (
            <Link to={linkFor(p.link || p.code)} key={p.code} className="card-warm p-5 flex flex-col" data-testid={`pathway-${p.code}`}>
              <div className="w-10 h-10 rounded-full bg-brand-navy text-white grid place-items-center font-display text-lg mb-3">{p.n}</div>
              <div className="eyebrow">{p.level}</div>
              <div className="font-display text-xl mt-1">{p.code}</div>
              <div className="text-xs font-semibold text-zinc-700 mt-1">{p.name}</div>
              <div className="text-xs text-zinc-600 mt-2 leading-relaxed flex-1">{p.blurb}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand-gold">
                View programme <ArrowRight size={12}/>
              </div>
            </Link>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mt-6">
          CSI certifications are awarded in accordance with the <a href="#cert-policy" className="underline">CSI Certification Policy</a>.
        </p>
      </section>

      {/* Qualification Components */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="eyebrow mb-1">5 Qualification Components</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-6">Qualification Components</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COMPONENTS.map((c) => (
            <div key={c.code} className="card-warm p-6" data-testid={`component-${c.code}`}>
              <div className="eyebrow mb-2">{c.badge}</div>
              <h3 className="font-display text-xl leading-tight">{c.name}</h3>
              <p className="text-sm text-zinc-700 mt-2">{c.desc}</p>
              <div className="text-xs text-zinc-600 mt-3 font-semibold">{c.meta}</div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <Link to={linkFor(c.code)} className="chip inline-flex items-center gap-1">View Programme <ArrowRight size={12}/></Link>
                <a href="mailto:info@convergedsecurity.es?subject=CSI%20Programme%20Enquiry" className="chip inline-flex items-center gap-1">
                  <Mail size={12}/> Enquire
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Policy */}
      <section id="cert-policy" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="eyebrow mb-1">Governance & Standards</div>
        <h2 className="font-display text-3xl sm:text-4xl">CSI Certification Policy 2.0</h2>
        <p className="text-zinc-700 max-w-3xl mt-3 text-sm sm:text-base">
          This policy governs the award, maintenance, and recognition of CSI certification components and the PGDCSL pathway.
          It applies to candidates, enrolled participants, certified professionals and PGDCSL candidates across the five-component professional qualification framework.
        </p>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {POLICY_SECTIONS.map((s) => (
            <div key={s.id} id={s.id} className="card-warm p-6" data-testid={`policy-${s.id}`}>
              <h3 className="font-display text-xl">{s.title}</h3>
              <ul className="mt-3 space-y-2 text-sm">
                {s.items.map((it, i) => (
                  <li key={i} className="flex gap-2">
                    <Check size={16} className="text-brand-gold shrink-0 mt-0.5"/>
                    <span className="text-zinc-800">
                      {it.k && <b className="text-brand-navy">{it.k}: </b>}
                      {it.v}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mt-6">
          CSI programme and certification policy alignment · Published by the Converged Security Institute · Administered by Prof. Dr. Vladimir Bunic · PGDCSL framework effective 1 January 2027.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-3xl bg-brand-navy text-white p-8 sm:p-12 text-center">
          <div className="eyebrow text-[var(--gold-soft)]">Take the next step</div>
          <h3 className="font-display text-3xl sm:text-4xl mt-2">Ready to advance your security career?</h3>
          <p className="text-zinc-300 max-w-2xl mx-auto mt-3 text-sm sm:text-base">
            Contact CSI to discuss the right qualification route, corporate training options, executive entry
            and Recognition of Prior Learning. Payment links and confirmed enrolment routes will be added once available.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <a href="mailto:info@convergedsecurity.es?subject=CSI%20Enquiry" className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold" data-testid="education-contact-btn">Contact us</a>
            <Link to={linkFor("PGDCSL")} className="chip px-5 py-3 bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="education-pgdcsl-btn">View PGDCSL</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
